import { TRosterItemPerson, TRosterUserPeerSupportCertificateInfo } from '@/types/Roster.types';
import React, { useCallback, useState } from 'react';
import { MAX_LINES_IN_ROSTER } from '@/constants';
import { BUTTON_HEIGHT } from '@/constants/dimensions';
import { useFieldArray, UseFormReturn } from 'react-hook-form';
import CustomButton from '@/components/atoms/CustomButton';
import svgIcons from '@/assets/svg/icons';
import colors from '@/theme/colors';

import { capitalize } from '@/utils/stringUtils';
import { USER_AFFILIATION_OPTIONS } from '@/constants';
import PeerSupportSwitch from '@/modules/organization/PeerSupportSwitch';
import OrganizationOnboardingPeerSupportModal from './OrganizationOnboardingPeerSupportModal';
import { Center, HStack, Input, Select, Text, VStack } from '@chakra-ui/react';
import { TPeerSupportCertificateInfoMap } from '@/store/useOrganizationOnboardingStore';
import { TUserAffiliation } from '@/types/User.types';
import { EMAIL_REGEX } from '@/constants/stringVars';
import PeerSupportLeadOnboardingIconTrigger from './PeerSupportLeadOnboardingIconTrigger';

export type TRosterUserType = Omit<TRosterItemPerson, 'Invitation' | 'Affiliation'> & { Affiliation: string };

const MANUAL_ROSTER_ITEM_FIELDS = [
  'First Name',
  'Last Name',
  'Email',
  'Phone',
  'Affiliation',
  'Peer Support',
] as (keyof TRosterUserType)[];

export const ROSTER_AFFILIATION_DEFAULT_VALUE = 'Select';

export const MANUAL_ROSTER_ITEM_DEFAULT_VALUES: TRosterUserType = {
  'First Name': '',
  'Last Name': '',
  Email: '',
  Phone: '',
  Affiliation: ROSTER_AFFILIATION_DEFAULT_VALUE,
  'Peer Support': false,
};

export interface RosterManualEntryFormProps {
  methods: UseFormReturn<{ rosterUsers?: TRosterUserType[] }, any, undefined>;
  peerSupportData: TPeerSupportCertificateInfoMap;
  setPeerSupportData: (newData: TPeerSupportCertificateInfoMap) => void;
}

const INIT_HOVERED_INDEX_VALUE = undefined;

const RosterManualEntryForm = ({ methods, peerSupportData, setPeerSupportData }: RosterManualEntryFormProps) => {
  const { control, formState, watch, register, setValue } = methods;

  const { errors, touchedFields } = formState;

  const rosterUsers = watch('rosterUsers');

  const [indexForPeerSupportModal, setIndexForPeerSupportModal] = useState<number>();

  const onClickUpgradePeerSupport = useCallback(
    (peerSupportUpgradeInfo: TRosterUserPeerSupportCertificateInfo) => {
      const prevCopy = peerSupportData ? new Map(peerSupportData) : new Map();
      prevCopy?.set(indexForPeerSupportModal!, peerSupportUpgradeInfo);

      setPeerSupportData(prevCopy);

      setValue(`rosterUsers.${indexForPeerSupportModal ?? 0}.Peer Support`, true);
    },
    [peerSupportData, indexForPeerSupportModal],
  );

  const {
    fields: rosterUsersFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: 'rosterUsers',
    rules: {
      validate: (value) => {
        const affiliationsOK = value.every((val) =>
          USER_AFFILIATION_OPTIONS.includes(val.Affiliation.toLowerCase() as TUserAffiliation),
        );
        const emailOK = value.every((val) => val.Email.match(EMAIL_REGEX));
        return affiliationsOK && emailOK;
      },
    },
  });

  const removeItem = (index: number) => {
    remove(index);
    const prevCopy = new Map(peerSupportData);
    prevCopy.delete(index);
    setPeerSupportData(prevCopy);
  };

  const [hoveredIndex, setHoveredIndex] = useState<number | undefined>(INIT_HOVERED_INDEX_VALUE);

  return (
    <VStack spacing={'15px'} marginTop={'40px'}>
      {rosterUsersFields?.map((field, index) => {
        const currentItemAffiliation = watch(`rosterUsers.${index}.Affiliation`);

        const isAffiliationTouched = touchedFields.rosterUsers?.[index]
          ? touchedFields.rosterUsers[index].Affiliation
          : false;

        const isCurrentItemPeerSupport = watch(`rosterUsers.${index}.Peer Support`);
        const isCurrentItemPeerSupportLead = peerSupportData?.get
          ? peerSupportData?.get(index)?.isPeerSupportLead
          : false;

        return (
          <HStack
            key={field.id}
            alignItems={'flex-start'}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(INIT_HOVERED_INDEX_VALUE)}
          >
            {MANUAL_ROSTER_ITEM_FIELDS.map((itemFieldName) => (
              <VStack key={itemFieldName} gap={'4px'}>
                {itemFieldName === 'Affiliation' && (
                  <Select
                    className="custom-input custom-select-padding"
                    focusBorderColor="transparent"
                    defaultValue={ROSTER_AFFILIATION_DEFAULT_VALUE}
                    isInvalid={!!errors?.rosterUsers?.[index]?.Affiliation}
                    _invalid={{ border: `1px solid ${colors.extra.red}!` }}
                    _valid={{ border: `1px solid ${colors.extra.black}` }}
                    style={{
                      fontSize: '14px',
                      width: 200,
                      color:
                        currentItemAffiliation === ROSTER_AFFILIATION_DEFAULT_VALUE
                          ? colors.text.mediumGray
                          : colors.extra.black,
                    }}
                    {...register(`rosterUsers.${index}.Affiliation`, {
                      required: { message: 'Affiliation is required', value: true },
                      validate: (value: string) => (value === '' ? 'Affiliation is required' : true),
                    })}
                    // this helps render border color as valid after selecting a value
                    data-state={
                      isAffiliationTouched
                        ? currentItemAffiliation === ROSTER_AFFILIATION_DEFAULT_VALUE
                          ? 'invalid'
                          : 'valid'
                        : ''
                    }
                  >
                    <option disabled>{ROSTER_AFFILIATION_DEFAULT_VALUE}</option>
                    {USER_AFFILIATION_OPTIONS.map((affiliationOption) => (
                      <option key={affiliationOption} value={affiliationOption}>
                        {capitalize(affiliationOption)}
                      </option>
                    ))}
                  </Select>
                )}

                {itemFieldName === 'Peer Support' && (
                  <Center width={'100px'} marginLeft={'10px'}>
                    <VStack height={'55px'} paddingTop={'1px'} alignItems={'start'}>
                      <Text variant={'urbanistSemiBoldRegular'} color={'text.mediumGray'}>
                        Peer Support
                      </Text>
                      <HStack gap={'10px'}>
                        <PeerSupportSwitch
                          {...register(`rosterUsers.${index}.${itemFieldName}`, { required: true })}
                          onChange={(checked) => {
                            if (checked) {
                              // we received a true value, so we show the modal to enter peer support certification data
                              setIndexForPeerSupportModal(index);
                            } else {
                              const prevCopy = new Map(peerSupportData);
                              prevCopy?.delete(index);

                              setPeerSupportData(prevCopy);

                              setValue(`rosterUsers.${index}.${itemFieldName}`, false);
                            }
                          }}
                          checked={isCurrentItemPeerSupport}
                        />
                        {
                          <PeerSupportLeadOnboardingIconTrigger
                            isPeerSupport={!!isCurrentItemPeerSupport}
                            isPeerSupportLead={!!isCurrentItemPeerSupportLead}
                            isRowHovered={hoveredIndex === index}
                            onConfirmPromote={() => {
                              const prevCopy = new Map(peerSupportData);
                              prevCopy.set(index, { ...prevCopy.get(index)!, isPeerSupportLead: true });

                              setValue(`rosterUsers.${index}.Peer Support Lead`, true);

                              setPeerSupportData(prevCopy);
                            }}
                            onConfirmDemote={() => {
                              const prevCopy = new Map(peerSupportData);
                              prevCopy.set(index, { ...prevCopy.get(index)!, isPeerSupportLead: false });

                              setValue(`rosterUsers.${index}.Peer Support Lead`, false);

                              setPeerSupportData(prevCopy);
                            }}
                          />
                        }
                      </HStack>
                    </VStack>
                  </Center>
                )}

                {itemFieldName !== 'Peer Support' && itemFieldName !== 'Affiliation' && (
                  <Input
                    {...register(`rosterUsers.${index}.${itemFieldName}`, { required: true })}
                    className="custom-input"
                    placeholder={itemFieldName}
                    _placeholder={{ fontWeight: '600', color: 'text.mediumGray' }}
                    isInvalid={!!errors?.rosterUsers?.[index]?.[itemFieldName]}
                    _invalid={{ border: `1px solid ${colors.extra.red}` }}
                    borderColor={
                      errors?.rosterUsers?.[index]?.[itemFieldName]
                        ? 'transparent'
                        : touchedFields.rosterUsers?.[index] && touchedFields.rosterUsers[index][itemFieldName]
                          ? 'text.'
                          : '#00417926'
                    }
                  />
                )}
                <Text
                  variant={'errorSmall'}
                  height={'10px'}
                  width={'100%'}
                  paddingLeft={itemFieldName === 'Affiliation' ? '8px' : '18px'}
                >
                  {errors?.rosterUsers?.[index]?.[itemFieldName] &&
                    errors?.rosterUsers?.[index]?.[itemFieldName].message}
                </Text>
              </VStack>
            ))}

            <VStack position={'relative'} data-group>
              <VStack position={'absolute'} left={'10px'} height={BUTTON_HEIGHT} justifyContent={'center'}>
                <CustomButton
                  onClick={() => removeItem(index)}
                  iconBeforeLabelSvgSize={16}
                  iconBeforeLabelSvgBoxHeight={14}
                  iconBeforeLabelSvgBoxWidth={12}
                  iconBeforeLabelSvgPath={svgIcons.trashCan}
                  iconBeforeLabelSvgColor={rosterUsersFields.length === 1 ? colors.extra.red70pct : colors.extra.red}
                  width={'30px'}
                  height={'30px'}
                  disabledColor="#fff"
                  labelStyle={{ marginLeft: 0, marginTop: '6px' }}
                  style={{ minWidth: '20px', borderRadius: '50%', paddingInline: 0 }}
                  disabled={rosterUsersFields.length === 1}
                />
                {rosterUsersFields.length === 1 && (
                  <Text
                    _groupHover={{ visibility: 'visible' }}
                    visibility={'hidden'}
                    variant={'urbanistSemiBoldRegular'}
                    position={'absolute'}
                    width={'135px'}
                    left={'55px'}
                    top={'15px'}
                    padding={'10px'}
                    borderRadius={'8px'}
                    backgroundColor={'extra.white'}
                    boxShadow={'0px 6.97px 34.86px 0px #00417930'}
                  >
                    You must have at least 1 member.
                  </Text>
                )}
              </VStack>
            </VStack>
          </HStack>
        );
      })}

      {/* Add new member button */}
      {/* TODO: what if roster file has more than MAX_LINES_IN_ROSTER lines */}
      {rosterUsersFields.length < MAX_LINES_IN_ROSTER ? (
        <CustomButton
          isTransparent
          onClick={() => append(MANUAL_ROSTER_ITEM_DEFAULT_VALUES)}
          style={{
            width: '200px',
            height: '55px',
          }}
          label={rosterUsersFields.length === 0 ? 'Add at least One Member' : 'Add Another Member'}
          labelHoverColor={colors.text.darkBlue}
          iconBeforeLabelSvgPath={svgIcons.plus}
        />
      ) : (
        <Text>You've reached the maximum number of users in your Roster.</Text>
      )}

      <OrganizationOnboardingPeerSupportModal
        peerSupportModalVisible={indexForPeerSupportModal !== undefined}
        setPeerSupportModalVisible={() => setIndexForPeerSupportModal(undefined)}
        onClickUpgrade={onClickUpgradePeerSupport}
        indexToUpgradePeerSupportData={indexForPeerSupportModal!}
        itemToUpgradeToPeerSupportName={
          indexForPeerSupportModal ? (rosterUsers?.[indexForPeerSupportModal]['First Name'] ?? 'user') : 'user'
        }
      />
    </VStack>
  );
};

export default RosterManualEntryForm;
